import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from 'services/auth/authService';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';

interface RegisterResponse {
  message: string;
  userId: string;
}

interface ErrorMessages {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

interface RegistrationProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const Registration: React.FC<RegistrationProps> = ({ setIsLoggedIn }) => {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const { isDark } = useTheme();

  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const errors: ErrorMessages = {};

    const nameRegex = /^[A-Za-z]+(\s[A-Za-z]+)*$/;
    if (!firstName) {
      errors.firstName = t('auth.registration.validation.firstNameRequired');
    } else if (firstName.trim().length < 2) {
      errors.firstName = t('auth.registration.validation.firstNameMinLength');
    } else if (!nameRegex.test(firstName.trim())) {
      errors.firstName = t('auth.registration.validation.firstNameLettersOnly');
    }

    if (!lastName) {
      errors.lastName = t('auth.registration.validation.lastNameRequired');
    } else if (lastName.trim().length < 2) {
      errors.lastName = t('auth.registration.validation.lastNameMinLength');
    } else if (!nameRegex.test(lastName.trim())) {
      errors.lastName = t('auth.registration.validation.lastNameLettersOnly');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errors.email = t('auth.registration.validation.emailRequired');
    } else if (!emailRegex.test(email)) {
      errors.email = t('auth.registration.validation.emailInvalid');
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
    if (!password) {
      errors.password = t('auth.registration.validation.passwordRequired');
    } else if (!passwordRegex.test(password)) {
      errors.password = t('auth.registration.validation.passwordRequirements');
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('auth.registration.validation.confirmPasswordRequired');
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.registration.validation.passwordsNotMatch');
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegistration = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLocalLoading(true);
      const response: AxiosResponse<RegisterResponse> = await authService.register(email, password, firstName.trim(), lastName.trim(), i18n.language);

      if (response.status === 201 && response.data.userId) {
        navigate('/verify-otp', { state: { userId: response.data.userId, email, password } });
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('auth.registration.registrationFailed');
      setErrorMessages({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLocalLoading(false);
    }
  };

  const inputClassName = `w-full px-3 py-2 text-base leading-6 rounded-fc outline-none transition-all ${
    isDark
      ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
      : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
  }`;

  const inputStyle = { '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties;

  const labelClassName = `block text-base font-medium leading-6 ${
    isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
  }`;

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-xl sm:text-2xl font-bold leading-7 sm:leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('auth.registration.title')}
          </h1>
          <p
            className={`text-base font-normal leading-6 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.registration.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleRegistration} className="w-full space-y-4">
          {/* First Name + Last Name row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-1">
              <label htmlFor="firstName" className={labelClassName}>
                {t('auth.registration.firstNameLabel')}
              </label>
              <input
                type="text"
                id="firstName"
                placeholder={t('auth.registration.firstNamePlaceholder')}
                value={firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                style={inputStyle}
                className={inputClassName}
              />
              {errorMessages.firstName && (
                <p className="text-xs text-red-500 mt-1">{errorMessages.firstName}</p>
              )}
            </div>

            <div className="flex-1 space-y-1">
              <label htmlFor="lastName" className={labelClassName}>
                {t('auth.registration.lastNameLabel')}
              </label>
              <input
                type="text"
                id="lastName"
                placeholder={t('auth.registration.lastNamePlaceholder')}
                value={lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
                style={inputStyle}
                className={inputClassName}
              />
              {errorMessages.lastName && (
                <p className="text-xs text-red-500 mt-1">{errorMessages.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label htmlFor="email" className={labelClassName}>
              {t('auth.registration.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t('auth.registration.emailPlaceholder')}
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.email && (
              <p className="text-xs text-red-500 mt-1">{errorMessages.email}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1">
            <label htmlFor="password" className={labelClassName}>
              {t('auth.registration.passwordLabel')}
            </label>
            <input
              type="password"
              id="password"
              placeholder={t('auth.registration.passwordPlaceholder')}
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.password && (
              <p className="text-xs text-red-500 mt-1">{errorMessages.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-1">
            <label htmlFor="confirmPassword" className={labelClassName}>
              {t('auth.registration.confirmPasswordLabel')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              placeholder={t('auth.registration.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">{errorMessages.confirmPassword}</p>
            )}
          </div>

          {/* General error */}
          {errorMessages.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessages.general}</p>
            </div>
          )}

          {/* Create Account Button */}
          <button
            type="submit"
            disabled={localLoading}
            className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {localLoading ? (
              <>
                <Spinner size="small" color="white" />
                <span>{t('auth.registration.registering')}</span>
              </>
            ) : (
              t('auth.registration.registerButton')
            )}
          </button>
        </form>

        {/* Sign In Link */}
        <p className="text-center">
          <span
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.registration.alreadyHaveAccount')}{' '}
          </span>
          <Link
            to="/login"
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-[#51A2FF]' : 'text-fc-blue'
            } hover:underline`}
          >
            {t('auth.registration.signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Registration;
